/**
 * @file PolicyLayout.tsx
 * @description Shared chrome for the public legal pages (/privacy, /terms).
 *   Brand + language toggle + back-to-home link in the header; long-form
 *   text in the body. Page content renders as a stack of <Section> blocks
 *   driven by the section keys passed in.
 * @module Components
 * @security No user input. Static text only; safe to expose without auth.
 */

import type { JSX, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageToggle } from './LanguageToggle';

export function PolicyLayout({
  titleKey,
  effectiveDateKey,
  versionKey,
  children,
}: {
  titleKey: string;
  effectiveDateKey: string;
  versionKey: string;
  children: ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  const date = t(effectiveDateKey);
  const version = t(versionKey);
  return (
    <main className="min-h-screen flex flex-col">
      <header className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
        <Link
          to="/"
          className="font-semibold rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          {t('landing.brand')}
        </Link>
        <LanguageToggle />
      </header>
      <section className="flex-1 px-6 py-8">
        <article className="mx-auto max-w-2xl space-y-6">
          <Link
            to="/"
            className="text-sm text-brand-strong hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            {t('common.back_to_home')}
          </Link>
          <h1 className="text-3xl font-bold">{t(titleKey)}</h1>
          <p className="text-sm text-slate-500">{t('common.effective_label', { date, version })}</p>
          <div className="space-y-6 leading-relaxed text-slate-700">{children}</div>
        </article>
      </section>
    </main>
  );
}

/**
 * One section of a policy: title + body. Body is a single string with
 * paragraph separators (\n\n) and optional bullet lines starting with
 * "• ". This keeps the i18n JSON readable without exploding into per-
 * paragraph keys.
 */
export function PolicySection({
  titleKey,
  bodyKey,
}: {
  titleKey: string;
  bodyKey: string;
}): JSX.Element {
  const { t } = useTranslation();
  const body = t(bodyKey);
  const paragraphs = body.split('\n\n');
  return (
    <section className="space-y-2">
      <h2 className="text-xl font-semibold">{t(titleKey)}</h2>
      {paragraphs.map((p, i) => (
        <Paragraph key={i} text={p} />
      ))}
    </section>
  );
}

function Paragraph({ text }: { text: string }): JSX.Element {
  const lines = text.split('\n');
  // If any line starts with the bullet marker, render the whole block
  // as a list; otherwise render as a plain paragraph. Bullet-only blocks
  // come up for the "data we collect" / "your rights" sections.
  const isList = lines.every((l) => l.trim().startsWith('• '));
  if (isList) {
    return (
      <ul className="list-disc list-inside space-y-1">
        {lines.map((l, i) => (
          <li key={i}>{l.replace(/^\s*•\s*/, '')}</li>
        ))}
      </ul>
    );
  }
  return <p>{text}</p>;
}
